import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { LoaderService } from '../../../../core/services/loader.service';
import { ProductConfigService, resolveDemoTheme } from '../../../../core/services/product-config.service';
import { CredeeDemoLogoComponent } from './credee-demo-logo.component';

/**
 * Product-branded global loader — shown only on the instant-demo routes.
 *
 * The app-wide loader (`app-global-loader`) is untouched everywhere else; on the
 * demo, {@link AppComponent} renders this in its place. It reacts to the same
 * signal (`LoaderService.state().show`), so every request that raises the modal
 * loader — research, token, a navigation — surfaces this instead.
 *
 * Branding follows the `?token=` product (or a later `credee:init` postMessage
 * from an embedding page): Practina shows its animated logo GIF, every other
 * product renders the `cd-logo` wordmark from its config displayName, inked in
 * the product's brand color. This overlay lives OUTSIDE the call component's
 * DOM, so it cannot inherit the `--cd-*` theme vars the call page sets on its
 * own host — the brand color is bound here directly instead.
 */
@Component({
  selector: 'app-credee-loader',
  standalone: true,
  imports: [CredeeDemoLogoComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (loader.state().show) {
      <div class="cdl-overlay" role="status" aria-live="polite" aria-busy="true"
        [style.--cd-logo-accent]="theme().logoAccent" [style.--cd-logo-body]="theme().logoBody">
        @if (gifSrc(); as gif) {
          <img [src]="gif" alt="Loading..." style="height: 60px; margin-bottom: 8px" />
        } @else {
          <cd-logo [productId]="productId()" [productName]="productName()" variant="wordmark"
            [wordmarkCase]="wordmarkCase()" [animate]="true" style="font-size: 24px"></cd-logo>
        }
        <p class="cdl-hint">Preparing your demo…</p>
        <span class="cdl-sr">Loading</span>
      </div>
    }
  `,
  styles: [
    `
      :host {
        display: contents;
      }

      .cdl-overlay {
        position: fixed;
        inset: 0;
        z-index: 9999;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 18px;
        background: radial-gradient(130% 120% at 50% 32%, #ffffff 0%, #f3f7fa 62%);
        backdrop-filter: blur(7px);
        -webkit-backdrop-filter: blur(7px);
        animation: cdl-fade 0.24s ease-out;
        font-family: 'Plus Jakarta Sans', system-ui, -apple-system, sans-serif;
      }

      .cdl-hint {
        margin: 0;
        font-size: 13px;
        color: #5f666d;
      }

      /* Screen-reader-only announcement. */
      .cdl-sr {
        position: absolute;
        width: 1px;
        height: 1px;
        padding: 0;
        margin: -1px;
        overflow: hidden;
        clip: rect(0, 0, 0, 0);
        white-space: nowrap;
        border: 0;
      }

      /* The wordmark's own bounce/ripple keyframes live in cd-logo, which renders it — including the
         prefers-reduced-motion calm-down. */
      @keyframes cdl-fade {
        from {
          opacity: 0;
        }
        to {
          opacity: 1;
        }
      }

      @media (prefers-reduced-motion: reduce) {
        .cdl-overlay {
          animation: none;
        }
      }
    `,
  ],
})
export class CredeeLoaderComponent {
  readonly loader = inject(LoaderService);
  private readonly configService = inject(ProductConfigService);

  /**
   * The product driving the branding. Read from `?token=`, and re-read when an embedding parent sends
   * `credee:init` — this component is mounted at app root, so it cannot take an input.
   */
  private readonly token = signal(this.readTokenFromUrl());

  constructor() {
    window.addEventListener('message', (e) => {
      if (e.data && typeof e.data === 'object' && e.data.type === 'credee:init') {
        const payload = e.data.params || e.data;
        this.token.set(payload.token || 'credee');
      }
    });
  }

  private readTokenFromUrl(): string {
    try {
      return new URLSearchParams(window.location.search).get('token') || 'credee';
    } catch {
      return 'credee';
    }
  }

  private readonly config = computed(() => this.configService.getProductConfig(this.token()));

  readonly productId = computed(() => this.config()?.id?.toLowerCase() || this.token().toLowerCase());
  readonly productName = computed(() => this.config()?.displayName || 'Credee');
  readonly wordmarkCase = computed(() => this.config()?.wordmarkCase ?? 'lower');

  /**
   * The overlay renders at app root, OUTSIDE `.cd-root`, so it inherits none of the demo's `--cd-*`
   * vars — it has to paint the wordmark colours itself.
   */
  readonly theme = computed(() => resolveDemoTheme(this.config()));

  /** A pre-rendered loader animation when the product has one; null → the bouncing-dot wordmark. */
  readonly gifSrc = computed(() => {
    const loaderCfg = this.config()?.demoLoader;
    return loaderCfg?.kind === 'gif' ? loaderCfg.src : null;
  });
}
